import { Injectable } from "@angular/core";
import { TermConfirmationViewModel } from "../../shared/models/term.model";
import { SetTermConfirmedModel } from "../../shared/models/term_confirmed.model";
import { RegisterationUserViewModel } from "../../shared/models/registration.model";
import { UserInfoViewModel, UserInfoData } from "../../shared/models/userInfo.model";
import { environment } from "../../../../environments/environment";
import { ContentType, LoginStatus } from "../../shared/contentTypes";
import { LikeService } from "../../services/like.service";
import { UappErrorCode } from "../../services/http.service";
import { UserHelperService } from "../../services/userHelper.service";
import { CircularInjectionHelper } from "../../services/circularInjectionHelper";

@Injectable({
  providedIn: "root"
})
export class RegistrationService {

  constructor(private _likeService: LikeService,
    private _objObsInjectHelper: CircularInjectionHelper,
    private _userHelperService: UserHelperService) { }

  /**
   * Register user
   * @param registerViewModel contains Username and password of user
   * @param termConfirmed term and conditions that user accept for resiteriing
   * @result true when user rigster successfully; false when user is exists on server; reject on server error
   */
  public RegisterUser(registerViewModel: RegisterationUserViewModel, termConfirmed: TermConfirmationViewModel[]): Promise<boolean> {
    const me = this;
    const setTermConfirmedModel = me.mapToSetTermConfirmedModelList(termConfirmed);
    return me._objObsInjectHelper.yourAppServer.Auth.RegisterCustomer(registerViewModel.userName, registerViewModel.password, setTermConfirmedModel);
  }

  /**
   * Register user with Google
   * @param idToken Google idToken
   * @param termConfirmed term and conditions that user accept for resiteriing
   * @result true when user rigster successfully; false when user is exists on server; reject on server error
   */
  public RegisterUserWithGoogle(idToken: string, clientID, termConfirmed: TermConfirmationViewModel[]): Promise<LoginStatus> {
    const me = this;
    const setTermConfirmedModel = me.mapToSetTermConfirmedModelList(termConfirmed);

    return new Promise<LoginStatus>((resolve, reject) => {
      me._objObsInjectHelper.yourAppServer.Auth.SignInCustomerWithGoogle(idToken, clientID, setTermConfirmedModel)
        .then((success) => {
          if (!success) {
            reject("Google sign-in failed");
            return;
          }
          me._userHelperService.IsRegistrationConfirmed()
            .then((response) => {
              if (response) {
                resolve(LoginStatus.LogedIn);
              } else {
                resolve(LoginStatus.IncompleteRegistration);
              }
            })
            .catch((e) => {
              me._objObsInjectHelper.coreLogService.error(
                "RegistrationService.RegisterUserWithGoogle",
                "_userHelperService.IsRegistrationConfirmed",
                e
              );
              reject(e);
            });
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * starts Google OAuth redirect flow (mobile).
   * resolves immediately after redirect is triggered.
   */
  public async StartGoogleOAuthRedirect(): Promise<LoginStatus> {
    const me = this;

    await me._objObsInjectHelper.yourAppServer.Auth.StartGoogleOAuthRedirectFlow();

    // flow continues in system browser
    return LoginStatus.IncompleteRegistration;
  }

  /**
 * Register user with Facebook
 * @param idToken Facebook App_id
 * @param termConfirmed term and conditions that user accept for resiteriing
 * @result true when user rigster successfully; false when user is exists on server; reject on server error
 */
  public RegisterUserWithFacebook(idToken: string, termConfirmed: TermConfirmationViewModel[]): Promise<LoginStatus> {
    const me = this;
    const setTermConfirmedModel = me.mapToSetTermConfirmedModelList(termConfirmed);
    return new Promise<LoginStatus>((resolve, reject) => {
      me._objObsInjectHelper.yourAppServer.Auth
        .SignInCustomerWithFacebook(idToken, setTermConfirmedModel)
        .then((success) => {
          if (!success) {
            reject("Facebook sign-in failed");
            return;
          }

          // wie im normalen Login & bei Google: setzt IsRegistered
          me._userHelperService.IsRegistrationConfirmed()
            .then((response) => {
              if (response) {
                resolve(LoginStatus.LogedIn);
              } else {
                resolve(LoginStatus.IncompleteRegistration);
              }
            })
            .catch((e) => {
              me._objObsInjectHelper.coreLogService.error(
                "RegistrationService.RegisterUserWithFacebook",
                "_userHelperService.IsRegistrationConfirmed",
                e
              );
              reject(e);
            });
        })
        .catch((error) => {
          me._objObsInjectHelper.coreLogService.error(
            "RegistrationService.RegisterUserWithFacebook",
            "_objObsInjectHelper.yourAppServer.Auth.SignInCustomerWithFacebook",
            error
          );
          reject(error);
        });
    });
  }

  /**
   * login user
   * @param loginViewModel contains Username and password of user
   * @result return a LoginStatus; reject when there is an Error on server
   */
  public LoginUser(loginViewModel: RegisterationUserViewModel): Promise<LoginStatus> {
    const me = this;
    return new Promise<LoginStatus>((resolve, reject) => {
      me._objObsInjectHelper.yourAppServer.Auth.SignInCustomer(loginViewModel.userName, loginViewModel.password)
        .then((success) => {
          if (success) {
            me._userHelperService.IsRegistrationConfirmed()
              .then((response) => {
                if (response) {
                  resolve(LoginStatus.LogedIn);
                } else {
                  resolve(LoginStatus.IncompleteRegistration);
                }
              })
              .catch((e) => {
                me._objObsInjectHelper.coreLogService.error("RegistrationService.LoginUser", "_userHelperService.IsRegistrationConfirmed", e);
                reject();
              });
          } else {
            resolve(LoginStatus.WrongUserOrPassword);
          }
        })
        .catch((error) => {
          if (error === UappErrorCode.NEED_VERYFIED) {
            resolve(LoginStatus.NotVerified);
          } else {
            me._objObsInjectHelper.coreLogService.error("RegistrationService.LoginUser", "_objObsInjectHelper.yourAppServer.Auth.SignInCustomer", error);
            reject();
          }
        });
    });
  }

  /**
   * create SetTermConfirmedModel for register User
   * @param viewModel instance of  TermConfirmationViewModel
   * @result instance of SetTermConfirmedModel
   */
  private mapToSetTermConfirmedModel(viewModel: TermConfirmationViewModel): SetTermConfirmedModel {
    const result: SetTermConfirmedModel = new SetTermConfirmedModel();
    result.id = viewModel.id;
    result.version_ymd = viewModel.versionYMD;
    result.version_hms = viewModel.versionHMS;
    return result;
  }

  /**
   * create a list of SetTermConfirmedModel for register User
   * @param viewModel a list of TermConfirmationViewModel
   * @result SetTermConfirmedModel
   */
  private mapToSetTermConfirmedModelList(viewModels: TermConfirmationViewModel[]): SetTermConfirmedModel[] {
    const me = this;
    const setTermConfirmedModels: SetTermConfirmedModel[] = [];
    viewModels.forEach(viewModel => {
      setTermConfirmedModels.push(me.mapToSetTermConfirmedModel(viewModel));
    });
    return setTermConfirmedModels;
  }

}